import { computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from "vue";
import getInstallmentPlans from "@/api/requests/getInstallmentPlans";
import type InstallmentPlan from "@/types/InstallmentPlan";

/**
 * The Installment plans of the Order for the card being typed in, asked for once its BIN is known and again whenever
 * the BIN or the Order changes.
 */
export function useInstallmentPlans(orderId: MaybeRefOrGetter<string>, cardNumber: MaybeRefOrGetter<string>) {
  const plans: Ref<InstallmentPlan[]> = ref([]);

  const loading: Ref<boolean> = ref(false);

  /** The plans of the card's BIN couldn't be had; none is offered rather than ones worked out here. */
  const failed: Ref<boolean> = ref(false);

  /** The card's first 6 digits, null while fewer have been typed. */
  const bin = computed<string | null>(() => {
    const digits = toValue(cardNumber).replace(/\D/g, "");

    return digits.length >= 6 ? digits.substring(0, 6) : null;
  });

  /** What the plans are asked for; another Order has another total, so other plans. */
  const question = computed<string | null>(() => bin.value === null ? null : `${toValue(orderId)}:${bin.value}`);

  async function load(): Promise<void> {
    const asked = question.value;
    const askedBin = bin.value;

    // The previous card's or Order's plans could differ from these
    plans.value = [];
    failed.value = false;
    loading.value = askedBin !== null;

    if (askedBin === null) return;

    try {
      const response = await getInstallmentPlans(toValue(orderId), askedBin);

      // The card or the Order changed meanwhile, their own answer is the one that counts
      if (asked !== question.value) return;

      plans.value = response;
    } catch {
      if (asked !== question.value) return;

      failed.value = true;
    }

    loading.value = false;
  }

  watch(question, load, { immediate: true });

  return {
    plans,
    loading,
    failed,
    retry: load,
  };
}
